
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, {useState, useRef} from 'react';
import {AspectRatio} from '../types';
import {ArrowPathIcon, DownloadIcon, SparklesIcon, FileImageIcon, PlusIcon} from './icons';
// @ts-ignore
import gifshot from 'gifshot';

interface VideoResultProps {
  videoUrl: string;
  parentVideoUrl?: string | null;
  onRetry: () => void;
  onNewVideo: () => void;
  onExtend: () => void;
  canExtend: boolean;
  aspectRatio: AspectRatio;
}

const VideoResult: React.FC<VideoResultProps> = ({
  videoUrl,
  parentVideoUrl,
  onRetry,
  onNewVideo,
  onExtend,
  canExtend,
  aspectRatio,
}) => {
  const isPortrait = aspectRatio === AspectRatio.PORTRAIT;
  const [isConvertingGif, setIsConvertingGif] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showParent, setShowParent] = useState(false);

  const handleDownloadGif = async (frames: number) => {
    if (!videoUrl) return;
    setIsConvertingGif(true);
    try {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = "anonymous";
      await new Promise((resolve) => {
        if (video.readyState >= 1) resolve(null);
        else video.onloadedmetadata = () => resolve(null);
      });
      const duration = video.duration;
      const width = isPortrait ? 360 : 640;
      const height = isPortrait ? 640 : 360;
      const step = duration / frames;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      const images: string[] = [];
      for (let i = 0; i < frames; i++) {
        const time = i * step;
        if (time > 0) {
          video.currentTime = time;
          await new Promise((resolve) => {
             const onSeeked = () => {
               video.removeEventListener('seeked', onSeeked);
               resolve(null);
             };
             video.addEventListener('seeked', onSeeked);
          });
        }
        if (ctx) {
          ctx.drawImage(video, 0, 0, width, height);
          images.push(canvas.toDataURL('image/jpeg', 0.8));
        }
      }
      gifshot.createGIF({
        images: images,
        interval: 0.1,
        gifWidth: width,
        gifHeight: height,
        numFrames: frames,
        sampleInterval: 10,
      }, (obj: any) => {
        if (!obj.error) {
          const link = document.createElement('a');
          link.href = obj.image;
          link.download = `veo-studio-creation-${(frames/10).toFixed(1)}s.gif`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error('GIF generation failed:', obj.error);
        }
        setIsConvertingGif(false);
      });
    } catch (error) {
      console.error('Error preparing GIF:', error);
      setIsConvertingGif(false);
    }
  };

  const getDurationLabel = (divisor: number) => {
    if (!videoDuration) return divisor === 1 ? '8s' : divisor === 2 ? '4s' : '2s';
    return `${Math.round(videoDuration / divisor)}s`;
  };

  const getFrames = (divisor: number) => {
    const duration = videoDuration || 8;
    return Math.floor((duration / divisor) * 10);
  }

  return (
    <div className="w-full relative flex flex-col items-center gap-6 p-8 bg-gray-800/50 rounded-2xl border border-gray-700 shadow-2xl overflow-visible my-4">
      <button
        onClick={onNewVideo}
        className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-purple-600/80 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition-all active:scale-95 shadow-lg shadow-purple-900/20 z-10"
      >
        <PlusIcon className="w-4 h-4" />
        New Video
      </button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-200">
          Your Creation is Ready!
        </h2>
        {parentVideoUrl && (
          <button 
            onClick={() => setShowParent(!showParent)}
            className="text-xs text-indigo-400 mt-2 hover:underline font-medium"
          >
            {showParent ? 'Hide original reference' : 'View original source video'}
          </button>
        )}
      </div>

      <div className="w-full flex flex-col lg:flex-row gap-6 items-center justify-center">
        {showParent && parentVideoUrl && (
           <div className={`shrink-0 flex flex-col gap-2 items-center ${isPortrait ? 'w-48' : 'w-72'}`}>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest self-start">Original Source</span>
            <div className={`w-full aspect-video rounded-lg overflow-hidden border border-gray-600 bg-black opacity-60 hover:opacity-100 transition-opacity cursor-pointer shadow-xl ${isPortrait ? 'aspect-[9/16]' : 'aspect-video'}`}>
               <video src={parentVideoUrl} muted loop autoPlay className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        <div 
          className={`relative group w-full ${
            isPortrait ? 'max-w-xs aspect-[9/16]' : 'max-w-2xl aspect-video'
          } rounded-xl overflow-hidden bg-black shadow-[0_0_50px_rgba(79,70,229,0.3)] border border-indigo-500/30 transition-all duration-500`}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay
            loop
            className="w-full h-full object-contain"
            onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3 w-full">
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded-lg transition-all"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Retry
        </button>
        
        <a
          href={videoUrl}
          download="veo-studio-creation.mp4"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg transition-all shadow-lg"
        >
          <DownloadIcon className="w-4 h-4" />
          Download MP4
        </a>

        <div className="relative group">
          <button
            disabled={isConvertingGif}
            onClick={() => handleDownloadGif(getFrames(1))}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-all shadow-lg disabled:opacity-50"
          >
            {isConvertingGif ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <FileImageIcon className="w-4 h-4" />
            )}
            GIF
          </button>
          
          {!isConvertingGif && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-30">
              <div className="p-2 text-[10px] text-gray-400 uppercase tracking-widest border-b border-gray-700 text-center font-bold">Speed Settings</div>
              <button onClick={() => handleDownloadGif(getFrames(4))} className="w-full text-left px-4 py-2 text-xs hover:bg-amber-600/50 flex justify-between"><span>{getDurationLabel(4)}</span> <span className="opacity-50">4x</span></button>
              <button onClick={() => handleDownloadGif(getFrames(2))} className="w-full text-left px-4 py-2 text-xs hover:bg-amber-600/50 flex justify-between"><span>{getDurationLabel(2)}</span> <span className="opacity-50">2x</span></button>
              <button onClick={() => handleDownloadGif(getFrames(1))} className="w-full text-left px-4 py-2 text-xs hover:bg-amber-600/50 flex justify-between"><span>{getDurationLabel(1)}</span> <span className="opacity-50">1x</span></button>
            </div>
          )}
        </div>

        {canExtend ? (
          <button
            onClick={onExtend}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg transition-all shadow-lg shadow-indigo-900/20"
          >
            <SparklesIcon className="w-4 h-4" />
            Extend Sequence
          </button>
        ) : (
          <button
            disabled
            className="flex items-center gap-2 px-4 py-2 bg-gray-700/50 text-gray-500 text-sm font-semibold rounded-lg cursor-not-allowed opacity-60 border border-gray-700"
          >
            <SparklesIcon className="w-4 h-4" />
            Extend (720p Only)
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoResult;
